/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import Loader from 'components/atoms/loader';
import { downloadBiddingFile } from 'services/actions/ai_management_files';
import { Container } from './styles';

const MIME_BY_EXTENSION: { [extension: string]: string } = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  bmp: 'image/bmp',
  webp: 'image/webp',
};

const PREVIEWABLE_MIMES = Object.values(MIME_BY_EXTENSION);

const extensionOf = (name: string) => name.split('.').pop()?.toLowerCase() ?? '';

const mimeOf = (name: string) => MIME_BY_EXTENSION[extensionOf(name)] ?? '';

interface AiManagementFilePreviewProps {
  name: string;
  downloadUrl: string;
  // Nome sem extensão confiável (documento do Gerenciar Documentos): o tipo vem da resposta do servidor.
  detectType?: boolean;
  // Página em que o PDF abre (visualizador do navegador).
  page?: number | null;
}

const AiManagementFilePreview: React.FC<AiManagementFilePreviewProps> = ({
  name,
  downloadUrl,
  detectType = false,
  page = null,
}) => {
  const declaredMime = detectType ? '' : mimeOf(name);
  const fetchable = detectType || !!declaredMime;
  const [mime, setMime] = useState(declaredMime);
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!fetchable) {
      setLoading(false);
      return undefined;
    }

    let active = true;
    let objectUrl: string | null = null;
    setLoading(true);
    setError(false);

    downloadBiddingFile(downloadUrl)
      .then((response) => {
        if (!active) return;
        const type = declaredMime || (response.data.type || '').split(';')[0].trim();
        if (!PREVIEWABLE_MIMES.includes(type)) {
          setMime('');
          return;
        }
        objectUrl = URL.createObjectURL(new Blob([response.data], { type }));
        setMime(type);
        setSrc(objectUrl);
      })
      .catch((err) => {
        console.error(`Erro ao abrir a pre-visualizacao de "${name}":`, err);
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [downloadUrl, name, declaredMime, fetchable]);

  const renderContent = () => {
    if (!fetchable) {
      return (
        <div className="file-preview-empty text-muted text-center py-4">
          Pré-visualização não disponível para arquivos .{extensionOf(name) || 'desconhecido'} —
          baixe o arquivo para abrir.
        </div>
      );
    }

    if (loading) {
      return (
        <div className="py-4">
          <Loader />
        </div>
      );
    }

    if (error) {
      return (
        <div className="alert alert-warning mb-0" role="alert">
          Não foi possível abrir a pré-visualização deste arquivo.
        </div>
      );
    }

    if (!mime) {
      return (
        <div className="file-preview-empty text-muted text-center py-4">
          Pré-visualização não disponível para este tipo de arquivo — baixe o arquivo para abrir.
        </div>
      );
    }

    if (!src) {
      return (
        <div className="alert alert-warning mb-0" role="alert">
          Não foi possível abrir a pré-visualização deste arquivo.
        </div>
      );
    }

    if (mime === 'application/pdf') {
      return (
        <iframe
          key={page ?? 0}
          className="file-preview-frame"
          src={page ? `${src}#page=${page}` : src}
          title={name}
        />
      );
    }

    return (
      <div className="file-preview-image text-center">
        <img src={src} alt={name} className="img-fluid" />
      </div>
    );
  };

  return <Container>{renderContent()}</Container>;
};

export default AiManagementFilePreview;
